import React, { useState } from 'react';
import { ChevronsRight, Phone, Mail, Check, ChevronDown, Asterisk } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { serviceOptions } from '../data/mock';

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', phone: '', product: '' });
  const [selected, setSelected] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const toggle = (opt) => setSelected((prev) => (prev.includes(opt) ? prev.filter((s) => s !== opt) : [...prev, opt]));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: 'Please add name and phone', description: 'We need these to call you back.' });
      return;
    }
    toast({
      title: 'Callback request received',
      description: `Thanks ${form.name}. Complise will reach out shortly.`,
    });
    setForm({ name: '', phone: '', product: '' });
    setSelected([]);
  };

  return (
    <section id="contact" className="relative py-20 lg:py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="relative rounded-[2rem] bg-[#0A0E1A] p-8 md:p-12 lg:p-16 overflow-hidden">
          {/* Decoration */}
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#C41E3A]/20 blur-3xl" />
          <div className="absolute top-8 right-8 w-64 h-64 rounded-full border border-white/10 spin-slow" />
          <div className="absolute top-16 right-16 w-48 h-48 rounded-full border border-white/5 spin-slow" style={{ animationDirection: 'reverse' }} />

          <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-sm font-medium text-[#F87171] mb-4">
                <Asterisk className="w-4 h-4" /> Free Consultation
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-white leading-[1.05] mb-6">
                Tell us your product. We&rsquo;ll map the approval path.
              </h2>
              <p className="text-white/70 leading-relaxed mb-10">
                Share the category, target market and current status. Complise will identify likely certifications, documentation needs, testing route and next steps.
              </p>

              <div className="space-y-4">
                <a href="tel:+918851590081" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 group-hover:bg-[#C41E3A] flex items-center justify-center transition-colors">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Call</div>
                    <div className="text-lg text-white font-semibold">8851590081</div>
                  </div>
                </a>
                <a href="mailto:info@compliseindia.com" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 group-hover:bg-[#C41E3A] flex items-center justify-center transition-colors">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Email</div>
                    <div className="text-lg text-white font-semibold">info@compliseindia.com</div>
                  </div>
                </a>
              </div>
            </div>

            <form onSubmit={submit} className="lg:col-span-7 bg-white/[0.04] backdrop-blur border border-white/10 rounded-[1.75rem] p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">Name</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    className="mt-2 bg-transparent border-white/10 focus:border-[#C41E3A] h-12 text-white placeholder:text-white/40 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">Phone</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91 —"
                    className="mt-2 bg-transparent border-white/10 focus:border-[#C41E3A] h-12 text-white placeholder:text-white/40 rounded-xl"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">Product / certification</label>
                <Textarea
                  value={form.product}
                  onChange={(e) => setForm({ ...form, product: e.target.value })}
                  placeholder="e.g. Bluetooth speaker, need WPC + BIS CRS"
                  className="mt-2 bg-transparent border-white/10 focus:border-[#C41E3A] min-h-24 text-white placeholder:text-white/40 rounded-xl"
                />
              </div>

              <div className="mt-5">
                <label className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">Services required</label>
                <button
                  type="button"
                  onClick={() => setPickerOpen(!pickerOpen)}
                  className="mt-2 w-full flex items-center justify-between px-4 h-12 border border-white/10 hover:border-[#C41E3A] rounded-xl bg-transparent text-left transition-colors"
                >
                  <span className="text-sm text-white/60">
                    {selected.length === 0 ? 'Select services required' : `${selected.length} selected`}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
                </button>
                {pickerOpen && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-1.5 p-4 rounded-xl bg-[#050810] border border-white/10 max-h-56 overflow-y-auto">
                    {serviceOptions.map((opt) => {
                      const isOn = selected.includes(opt);
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => toggle(opt)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                            isOn ? 'bg-[#C41E3A]/20 text-white' : 'text-white/60 hover:bg-white/5'
                          }`}
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center ${isOn ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-white/20'}`}>
                            {isOn && <Check className="w-3 h-3 text-white" />}
                          </span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                )}
                {selected.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selected.map((s) => (
                      <span key={s} className="text-xs px-3 py-1 rounded-full bg-[#C41E3A]/20 text-[#F87171] border border-[#C41E3A]/30">{s}</span>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="pill-btn group mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C41E3A] hover:bg-[#A81830] text-white h-14 text-sm font-semibold transition-colors"
              >
                Request Callback
                <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center pill-arrow">
                  <ChevronsRight className="w-3.5 h-3.5 text-[#C41E3A]" />
                </span>
              </button>
              <p className="mt-4 text-[11px] text-white/40 text-center">By submitting you agree to be contacted about your compliance requirement.</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
